import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EventType, useAnalytics } from '../../Analytics';
import HomeIcon from '../../../assets/svg/HomeICon';
import RewardIcon from '../../../assets/svg/RewardIcon';
import MoreIcon from '../../../assets/svg/MoreIcon';
import OfferIcon from '../../../assets/svg/OfferIcon';
import ManagedCard from '../../../assets/svg/ManagedCard';
import {
  ViewEPPIcon,
  ViewServiceRequestIcon,
  SendAndViewRequestIcon,
} from '../../../assets/svg/ViewEPPIcon';
import { ManageAlertsIcon } from '../../../assets/svg/ManageAlertsIcon';
import { ManagePasskeyIcon } from '../../../assets/svg/ManagePasskeyIcon';
import { ContactUsIcon } from '../../../assets/svg/ContactUsIcon';
import { PreauthorizedDebitIcon } from '../../../assets/svg/PreauthorizedDebitIcon';
import {
  CardFeaturesIcon,
  UnlinkRogersAcc,
} from '../../../assets/svg/CardFeaturesIcon';
import { FAQsIcon, LegalPrivacyIcon } from '../../../assets/svg/FAQsIcon';
import FAQsModal from '../../../components/FAQsModal';
import LegalPrivacyModal from '../../../components/LegalPrivacyModal';
import Modal from 'react-modal';
import ContactUs from '../../../screens/ContactUs';
import UnlinkRogersBenefits from '../../../screens/UnlinkAccount';
import SuccessModel from '../../../components/SuccessModel';
import { useDispatch, useSelector } from 'react-redux';
import {
  bankAccountDetailRequest,
  fetchRewardAccountRequest,
  PasskeyFeatureFlagInput,
  RootState,
  resolvePasskeyFeatureFlags,
  unlinkAccountRequest,
} from '@rb-serve-app/core';
import CommanModal from '../../../components/CommanModal';
import { logBrazeCustomEvent } from '../../../config/braze';

type MenuIconProps = {
  fill?: string;
  hoverFill?: string;
};

type SideMenuContentfulData = {
  homeLogoTitle?: string;
  manageCardLogoTitle?: string;
  rewardsLogoTitle?: string;
  offersLogoTitle?: string;
  moreLogoTitle?: string;
  rbNotifications?: {
    enable_notification_popup_title?: string;
    enable_notification_popup_primary_button?: string;
  };
  moreMenu?: {
    contact_us?: string;
    manage_alerts?: string;
    enable_push_notifications?: string;
    pre_autorised_debit?: string;
    card_feature?: string;
    send_and_view_msgs?: string;
    faqs?: string;
    legal_privacy?: string;
    view_service_req?: string;
    unLink_rogers_acc?: string;
    view_epp?: string;
    manage_passkey?: string;
    [key: string]: string | undefined;
  };
};

type SideMenuFeatureFlaggingData = {
  passkey?: PasskeyFeatureFlagInput;
  webMore?: {
    contact_us?: boolean;
    manage_alerts?: boolean;
    enable_push_notifications?: boolean;
    notifications?: boolean;
    pre_authorized_debit?: boolean;
    card_features?: boolean;
    send_and_view_msgs?: boolean;
    faq?: boolean;
    legal_privacy?: boolean;
    view_service_request?: boolean;
    unlink_rogers_benefits?: boolean;
    manage_passkey?: boolean;
    [key: string]: boolean | undefined;
  };
  navigation?: {
    offersFeatureFlaggedBasedOnProductType?: Record<string, boolean>;
  };
};

type DerogatoryData = {
  rbMoreDerogatoryStatusTitle?: string;
  rbMoreDerogatoryStatusMessage?: string;
  rbMoreBtnOk?: string;
};

type MoreMenuItem = {
  to?: string;
  label: string;
  Icon: React.ComponentType<MenuIconProps>;
  featureFlag?: boolean;
  onClick?: () => void;
};

type MainMenuItem = {
  to?: string;
  label: string;
  Icon: React.ComponentType<MenuIconProps>;
  subRoutes?: string[];
  featureFlag?: boolean;
};

type SideMenuProps = {
  contentfulData: SideMenuContentfulData;
  featureFlaggingData: SideMenuFeatureFlaggingData;
  setShowFeatureFlagMessage: React.Dispatch<React.SetStateAction<boolean>>;
  isLinkAcc: boolean;
  isAccountRestricted: () => boolean;
  assets: { we_are_sorry?: string };
  derogatoryData: DerogatoryData;
  isEppVisible: boolean;
  isPasskeyRegistered?: boolean;
  isUnlinkModal: boolean;
  setShowUnlinkModal: React.Dispatch<React.SetStateAction<boolean>>;
};

const SideMenu = ({
  contentfulData,
  featureFlaggingData,
  setShowFeatureFlagMessage,
  isLinkAcc,
  isAccountRestricted,
  assets,
  derogatoryData,
  isEppVisible,
  isPasskeyRegistered,
  isUnlinkModal,
  setShowUnlinkModal,
}: SideMenuProps) => {
  const { contentfulData: contentful } = useSelector(
    (state: RootState) => state.contentful
  );

  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const [activeMenuItem, setActiveMenuItem] = useState('');
  const [activeMoreMenuItems, setActiveMoreMenuItems] = useState(false);
  const [isHovered, setIsHovered] = useState<string | null>(null);
  const [isFaqsModalVisible, setIsFaqsModalVisible] = useState(false);
  const [isLegalPrivacyModalVisible, setIsLegalPrivacyModalVisible] =
    useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [isContactUsOpened, setIsContactUsOpened] = useState(false);
  const [showNotificationPopup, setShowNotificationPopup] = useState(false);
  const moreMenuModalRef = useRef<HTMLDivElement>(null);
  const moreButtonRef = useRef<HTMLDivElement>(null);
  const currentMenuItemRef = useRef<string>('');

  const {
    nextLinkAttemptDate,
    rciAccountDetails,
    linkingStatus,
    loading,
    status,
    detail,
    error,
  } = useSelector((state: RootState) => state.banLinkage);

  const { accountId, customerId, accountDetails } = useSelector(
    (state: RootState) => state.user
  );
  const productName = accountDetails?.productName;
  const isOffersEnabledForProduct = Boolean(
    productName
      ? featureFlaggingData?.navigation
          ?.offersFeatureFlaggedBasedOnProductType?.[productName]
      : false
  );

  const { entries } = contentful;
  const { rb_roam_like_home, rb_offers } = entries;
  const content = rb_roam_like_home?.rbUnlinkRogersBenefits;
  const eligibleContent = rb_roam_like_home?.rbRoamLikeHomeEligible;

  const [isUnlinkAccountPopup, setUnlinkAccountPopup] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [linkageStatusText, setLinkageStatusText] = useState('');
  const [isUnlinkError, setIsUnlinkError] = useState(false);
  const [nextLinkageDate, setNextLinkageDate] = useState('');

  const handleCancel = () => {
    setShowConfirmModal(false);
  };

  useEffect(() => {
    if (isUnlinking && !loading) {
      setIsUnlinking(false);
      console.log('Reaching here ---', error);
      if (
        linkingStatus === true ||
        status === 'Success' ||
        detail?.includes(content?.successfullyUnlinked)
      ) {
        setLinkageStatusText(
          content?.linkedSubTitle || 'Your card is unlinked.'
        );
        setShowSuccessModal(true);
        dispatch(bankAccountDetailRequest({ accId: accountId }));
        dispatch(
          fetchRewardAccountRequest({ user_id: accountId.replace(/^0+/, '') })
        );
      }
      //   else if (
      //     error?.status !== 'Success' ||
      //     error?.detail?.includes('Error')
      //   )
      else if (error) {
        setIsUnlinkError(true);
      }
    }
  }, [isUnlinking, loading, linkingStatus, status, detail]);

  const dispatch = useDispatch();

  const handleConfirm = () => {
    if (!loading) {
      setShowConfirmModal(false);
    }

    if (rciAccountDetails?.bankAccountResponse) {
      setIsUnlinking(true);
      const payload = {
        ban: rciAccountDetails?.bankAccountResponse?.ban,
        accountId: accountId,
        customerId: customerId,
      };
      console.log(payload, 'payload');
      //  setShowSuccessModal(true);
      // setIsUnlinkError(true);
      dispatch(
        unlinkAccountRequest({
          ban: rciAccountDetails?.bankAccountResponse?.ban,
          accountId: accountId,
          customerId: customerId,
        })
      );
    } else {
      console.error('Missing required BAN information for unlinking');
    }
  };

  const handleEnablePushNotifications = useCallback(async () => {
    if (typeof Notification === 'undefined') {
      setShowNotificationPopup(true);
      return;
    }

    if (Notification.permission === 'granted') {
      return;
    }

    if (Notification.permission === 'default') {
      let didRequestPermission = false;

      try {
        const { requestBrazePushPermission } =
          await import('../../../config/braze');
        didRequestPermission = requestBrazePushPermission();
      } catch (error) {
        console.error('[Braze] failed to request web push permission:', error);
      }

      const permissionAfterRequest = globalThis.Notification.permission;

      if (!didRequestPermission && permissionAfterRequest === 'denied') {
        setShowNotificationPopup(true);
      }

      return;
    }

    setShowNotificationPopup(true);
  }, []);

  const shouldShowEnablePushNotifications =
    typeof Notification === 'undefined' ||
    Notification.permission !== 'granted';

  const moreMenuItemsList = useMemo(() => {
    const menuItems: MoreMenuItem[] = [
      {
        onClick: () => setIsContactUsOpened(true),
        label: contentfulData?.moreMenu?.contact_us || '',
        Icon: ContactUsIcon,
        featureFlag: featureFlaggingData?.webMore?.contact_us,
      },
      {
        to: '/pre-authorized-debit',
        label: contentfulData?.moreMenu?.pre_autorised_debit || '',
        Icon: PreauthorizedDebitIcon,
        featureFlag: featureFlaggingData?.webMore?.pre_authorized_debit,
      },
      ...(isPasskeyRegistered
        ? [
            {
              to: '/manage-passkey',
              label: contentfulData?.moreMenu?.manage_passkey || '',
              Icon: ManagePasskeyIcon,
              featureFlag:
                resolvePasskeyFeatureFlags(featureFlaggingData).web
                  .deletionEnabled === false
                  ? false
                  : featureFlaggingData?.webMore?.manage_passkey,
            },
          ]
        : []),
      {
        to: '/card-features',
        label: contentfulData?.moreMenu?.card_feature || '',
        Icon: CardFeaturesIcon,
        featureFlag: featureFlaggingData?.webMore?.card_features,
      },
      {
        to: '/secure-messages',
        label: contentfulData?.moreMenu?.send_and_view_msgs || '',
        Icon: SendAndViewRequestIcon,
        featureFlag: featureFlaggingData?.webMore?.send_and_view_msgs,
      },
      {
        label: contentfulData?.moreMenu?.faqs || '',
        Icon: FAQsIcon,
        featureFlag: featureFlaggingData?.webMore?.faq,
        onClick: () => setIsFaqsModalVisible(true),
      },
      {
        label: contentfulData?.moreMenu?.legal_privacy || '',
        Icon: LegalPrivacyIcon,
        featureFlag: featureFlaggingData?.webMore?.legal_privacy,
        onClick: () => setIsLegalPrivacyModalVisible(true),
      },
      {
        to: '/service-request',
        label: contentfulData?.moreMenu?.view_service_req || '',
        Icon: ViewServiceRequestIcon,
        featureFlag: featureFlaggingData?.webMore?.view_service_request,
      },
    ];

    if (shouldShowEnablePushNotifications) {
      menuItems.splice(1, 0, {
        label:
          contentfulData?.moreMenu?.enable_push_notifications ||
          'Enable Push Notifications',
        Icon: ManageAlertsIcon,
        featureFlag:
          featureFlaggingData?.webMore?.enable_push_notifications ??
          featureFlaggingData?.webMore?.notifications,
        onClick: () => {
          void handleEnablePushNotifications();
        },
      });
    }

    if (isLinkAcc) {
      menuItems.push({
        to: undefined,
        featureFlag: featureFlaggingData?.webMore?.unlink_rogers_benefits,
        label: contentfulData?.moreMenu?.unLink_rogers_acc || '',
        Icon: UnlinkRogersAcc,
        onClick: () => setUnlinkAccountPopup(true),
      });
    }

    if (contentfulData?.moreMenu?.manage_alerts) {
      if (!isAccountRestricted()) {
        menuItems.splice(0, 0, {
          to: '/manage-alerts',
          label: contentfulData?.moreMenu?.manage_alerts || '',
          Icon: ManageAlertsIcon,
          featureFlag: featureFlaggingData?.webMore?.manage_alerts,
        });
      } else {
        menuItems.splice(0, 0, {
          onClick: () => setShowPopup(true),
          label: contentfulData?.moreMenu?.manage_alerts || '',
          Icon: ManageAlertsIcon,
          featureFlag: featureFlaggingData?.webMore?.manage_alerts,
        });
      }
    }

    return menuItems;
  }, [
    contentfulData,
    featureFlaggingData,
    handleEnablePushNotifications,
    shouldShowEnablePushNotifications,
    isLinkAcc,
    isPasskeyRegistered,
    isAccountRestricted,
  ]);

  const menuItemsList = useMemo(() => {
    const mainMenuItems: MainMenuItem[] = [
      {
        to: '/home',
        label: contentfulData?.homeLogoTitle || '',
        Icon: HomeIcon,
        subRoutes: [
          'account-details',
          'statements',
          'transactions',
          'my-profile',
        ],
      },
      {
        to: '/manage-card',
        label: contentfulData?.manageCardLogoTitle || '',
        Icon: ManagedCard,
        subRoutes: [
          'lock-unlock',
          'request-supplementary',
          'supplementary-details',
          'account-protection',
        ],
      },
      {
        to: '/rewards',
        label: contentfulData?.rewardsLogoTitle || '',
        Icon: RewardIcon,
        subRoutes: [
          'view-rewards',
          'redeem-rewards',
          'roam-like-home',
          'link-account',
          'world-legend-credit-redemption',
        ],
      },
      {
        to: '#',
        label: contentfulData?.moreLogoTitle || '',
        Icon: MoreIcon,
      },
    ];

    // Add offers menu item only if HideViewAllOffersWeb is not true
    //if (!rb_offers?.rbOffersFlag?.HideViewAllOffersWeb) featureFlaggingData?.navigation?.offers {

    if (!rb_offers?.rbOffersFlag?.HideViewAllOffersWeb) {
      mainMenuItems.splice(1, 0, {
        to: `${isOffersEnabledForProduct ? '/offers' : '#'} `,
        label: contentfulData?.offersLogoTitle || '',
        Icon: OfferIcon,
        featureFlag: isOffersEnabledForProduct,
      });
    }

    if (isEppVisible) {
      // Insert EPP before the last item (More), regardless of offers presence
      const moreIndex = mainMenuItems.findIndex(
        (item) => item.label === contentfulData?.moreLogoTitle
      );
      const insertIndex =
        moreIndex !== -1 ? moreIndex : mainMenuItems.length - 1;
      mainMenuItems.splice(insertIndex, 0, {
        to: '/equal-payment-plan',
        label: contentfulData?.moreMenu?.view_epp || '',
        Icon: ViewEPPIcon,
      });
    }
    return mainMenuItems;
  }, [contentfulData, featureFlaggingData, isEppVisible, rb_offers]);

  const getMoreMenuItem = useCallback(() => {
    const selectedItem = moreMenuItemsList.find(
      (m) => m.to === window.location.pathname
    );
    return selectedItem !== undefined; // returns true or false
  }, [moreMenuItemsList]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        moreMenuModalRef.current &&
        !moreMenuModalRef.current.contains(event.target as Node) &&
        moreButtonRef.current &&
        !moreButtonRef.current.contains(event.target as Node)
      ) {
        setActiveMoreMenuItems(false); // Close the submenu if clicked outside
        const isMoreMenuItemSelected = getMoreMenuItem();
        if (currentMenuItemRef.current && !isMoreMenuItemSelected) {
          setActiveMenuItem(currentMenuItemRef.current);
        }
      }
    };
    // Effect to handle clicks outside the submenu
    document.body.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.body.removeEventListener('mousedown', handleClickOutside);
    };
  }, [getMoreMenuItem]);

  useEffect(() => {
    if (window.location.pathname && !activeMoreMenuItems) {
      const pathName = window.location.pathname.split('/')[1];
      const menuName = menuItemsList.find(
        (m) => m.to?.includes(pathName) || m.subRoutes?.includes(pathName)
      )?.label;
      const isMoreMenuItemSelected = getMoreMenuItem();

      if (isMoreMenuItemSelected) {
        setActiveMenuItem(contentfulData?.moreLogoTitle || '');
      } else {
        setActiveMenuItem(menuName || '');
      }
    }
  }, [
    window.location.pathname,
    contentfulData,
    getMoreMenuItem,
    menuItemsList,
    activeMoreMenuItems,
  ]);

  useEffect(() => {
    if (activeMenuItem !== contentfulData?.moreLogoTitle) {
      currentMenuItemRef.current = activeMenuItem;
    }
  }, [activeMenuItem]);

  const handleMenuClick = (
    menuItem: string,
    featureFlag?: boolean,
    to?: string,
    onClick?: () => void
  ) => {
    if (menuItem === contentfulData?.moreLogoTitle && !activeMoreMenuItems) {
      logBrazeCustomEvent('viewed_more');
    }

    if (featureFlag === false) {
      if (window.scrollY !== 0) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
      setShowFeatureFlagMessage(true);
      setTimeout(() => setShowFeatureFlagMessage(false), 3000);
      setActiveMenuItem(menuItem);
      if (menuItem === contentfulData?.moreLogoTitle) {
        setActiveMoreMenuItems(!activeMoreMenuItems);
      }
      return;
    }
    setActiveMenuItem(menuItem);
    if (menuItem === contentfulData?.moreLogoTitle) {
      setActiveMoreMenuItems(!activeMoreMenuItems);
    } else if (to) {
      navigate(to);
    }
    if (onClick) {
      onClick();
    }
  };

  const getButtonClass = (featureFlag?: boolean) => {
    return featureFlag === false
      ? 'hover:bg-gray-300 hover:text-gray-300'
      : 'hover:bg-primary-red hover:text-white';
  };

  const normalizeLabel = (label?: string) => label?.toLowerCase() || '';

  const isActiveLabel = (label?: string) =>
    normalizeLabel(activeMenuItem) === normalizeLabel(label);

  const getIconFill = (featureFlag: boolean | undefined, label?: string) => {
    if (featureFlag === false) {
      return '#ccc';
    }

    if (isHovered === label || isActiveLabel(label)) {
      return '#fff';
    }

    return '#000';
  };

  const trackMenuClick = (label?: string, isMoreMenu = false) => {
    if (!label || isActiveLabel(label)) {
      return;
    }

    const path = isMoreMenu
      ? `dashboard|navigation pane|more|${label.toLocaleLowerCase()}`
      : `dashboard|navigation pane|${label.toLocaleLowerCase()}`;

    trackEvent(
      EventType.CLICK_EVENT,
      'dashboard',
      globalThis.location?.href ?? '',
      path
    );
  };

  const handleOk = () => {
    setShowSuccessModal(false);
    setIsUnlinkError(false);
  };
  return (
    <aside
      className="hidden sm:hidden md:block bg-white text-black w-20 flex flex-col items-center shadow-[0px_4px_4px_0px_rgba(0,0,0,0.10)] flex-shrink-0"
      aria-label="Sidebar Navigation"
    >
      <nav aria-label="Main Menu">
        {menuItemsList.map((menuItem) => {
          const { to, Icon, label, featureFlag } = menuItem;

          return (
            <div
              key={label}
              ref={
                label.toLowerCase() === 'more' || label.toLowerCase() === 'plus'
                  ? moreButtonRef
                  : null
              }
            >
              <Link
                to={to || '#'}
                className={`flex flex-col items-center space-y-1 p-2 ${
                  isActiveLabel(label)
                    ? 'bg-primary-red text-white'
                    : getButtonClass(featureFlag)
                }`}
                aria-label={label}
                onClick={() => {
                  trackMenuClick(label);
                  handleMenuClick(label, featureFlag, to);
                }}
                onMouseEnter={() => setIsHovered(label)}
                onMouseLeave={() => setIsHovered(null)}
              >
                <Icon fill={getIconFill(featureFlag, label)} />
                <span
                  className={`text-xs ${
                    label?.toLowerCase() ===
                      contentfulData?.manageCardLogoTitle?.toLowerCase() ||
                    contentfulData?.moreMenu?.view_epp?.toLowerCase()
                      ? 'text-center'
                      : ''
                  } ${featureFlag === false ? 'text-gray-300' : ''}`}
                >
                  {label}
                </span>
              </Link>
            </div>
          );
        })}
        {activeMoreMenuItems && (
          <div
            ref={moreMenuModalRef}
            className="grid grid-cols-3 gap-4 ml-4 p-4 absolute w-80 transform translate-x-16 top-32 text-black bg-white border-light-gray-border rounded-md shadow-ld border z-50"
          >
            {moreMenuItemsList.map(
              ({ to, label, Icon, featureFlag, onClick }) => (
                <Link
                  key={label}
                  to={to || '#'}
                  aria-label={label}
                  onClick={(e) => {
                    if (featureFlag === false) {
                      e.preventDefault();
                    }
                    trackMenuClick(label, true);
                    handleMenuClick(
                      contentfulData?.moreLogoTitle || '',
                      featureFlag,
                      to,
                      onClick
                    );
                  }}
                  className={`flex flex-col items-center space-y-1 p-2 transition duration-200 hover:bg-gray-50`}
                  onMouseEnter={() => setIsHovered(label)}
                  onMouseLeave={() => setIsHovered(null)}
                >
                  <Icon
                    fill={getIconFill(featureFlag, label)}
                    hoverFill={
                      isHovered === label
                        ? featureFlag === false
                          ? ''
                          : '#da291c'
                        : '#FAFAFA'
                    }
                  />
                  <span className="text-center">{label}</span>
                </Link>
              )
            )}
          </div>
        )}
      </nav>
      {isFaqsModalVisible && (
        <FAQsModal
          isFAQsModalOpen={isFaqsModalVisible}
          setIsFAQsModalOpen={setIsFaqsModalVisible}
        />
      )}
      {isLegalPrivacyModalVisible && (
        <LegalPrivacyModal
          isLegalPrivacyModalOpen={isLegalPrivacyModalVisible}
          setIsLegalPrivacyModalOpen={setIsLegalPrivacyModalVisible}
        />
      )}
      {showPopup && (
        <Modal
          isOpen={showPopup}
          contentLabel="derogatoryPopup"
          className="fixed p-0 w-[370px] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 outline-none"
          overlayClassName="fixed inset-0 bg-black bg-opacity-50"
        >
          <div className="flex flex-col justify-center items-center gap-[20px] p-[20px] rounded-lg bg-white overflow-auto relative">
            <img
              src={assets?.we_are_sorry}
              alt="sorryIcon"
              className="w-20 h-20"
            />
            <h3
              className="text-[#1F1F1F] text-[18px] font-[500] leading-[25px] tracking-[0.068px] text-center"
              aria-label={derogatoryData.rbMoreDerogatoryStatusTitle}
            >
              {derogatoryData.rbMoreDerogatoryStatusTitle}
            </h3>
            <p
              className="text-[#1F1F1F] text-[16px] font-[400] leading-[21px] tracking-[-0.051px] text-center"
              aria-label={derogatoryData.rbMoreDerogatoryStatusMessage}
            >
              {derogatoryData.rbMoreDerogatoryStatusMessage}
            </p>
            <button
              aria-label={derogatoryData.rbMoreBtnOk}
              type="button"
              onClick={() => {
                setShowPopup(false);
              }}
              className="text-center flex-1 items-center align-center text-white px-[20px] py-[12px] bg-primary-red w-full"
            >
              {derogatoryData.rbMoreBtnOk}
            </button>
          </div>
        </Modal>
      )}
      {isContactUsOpened && (
        <ContactUs
          isContactUsOpened={isContactUsOpened}
          setIsContactUsOpened={setIsContactUsOpened}
        />
      )}
      {showNotificationPopup && (
        <CommanModal
          isOpen={showNotificationPopup}
          onClose={() => setShowNotificationPopup(false)}
          closeIcon={false}
          buttonText={
            contentfulData?.rbNotifications
              ?.enable_notification_popup_primary_button
          }
          jsxContent={
            <p className="text-[#1F1F1F] text-[16px] font-[400] leading-[21px] tracking-[-0.051px] text-center">
              {contentfulData?.rbNotifications?.enable_notification_popup_title}
            </p>
          }
        />
      )}
      {isUnlinkAccountPopup && (
        <UnlinkRogersBenefits
          isUnlinkAccountPopup={isUnlinkAccountPopup}
          setUnlinkAccountPopup={setUnlinkAccountPopup}
          setShowConfirmModal={setShowConfirmModal}
          setShowSuccessModal={setShowSuccessModal}
          setNextLinkageDate={setNextLinkageDate}
          nextLinkageDate={nextLinkageDate}
          setLinkageStatusText={setLinkageStatusText}
          setIsUnlinkError={setIsUnlinkError}
        />
      )}

      <CommanModal
        isOpen={showConfirmModal}
        onClose={handleCancel}
        onExtraButtonClick={handleConfirm}
        jsxContent={
          <div className="text-center">
            <div className="mb-4 text-[#1F1F1F] text-[18px] font-medium ">
              {content?.confirmationPopupTitle}
            </div>
            <p className="mb-[40px]">{''}</p>
          </div>
        }
        buttonText={content?.btnConfirmationPopupCancel}
        extraButtonText={
          loading ? 'Loading...' : content?.btnConfirmationPopupConfirm
        }
        buttonClassName="flex-col-reverse"
        closeIcon={false}
      />

      {showSuccessModal && (
        <SuccessModel
          isOpen={showSuccessModal}
          title={linkageStatusText}
          subTitle={
            content?.successPopupSubTitle + nextLinkageDate ||
            nextLinkAttemptDate ||
            ''
          }
          successIcon={true}
          closeModal={handleOk}
          errorIcon={false}
        />
      )}

      {isUnlinkError && (
        <SuccessModel
          isOpen={isUnlinkError}
          title={eligibleContent?.roamLikeHomeError?.title}
          subTitle={eligibleContent?.roamLikeHomeError?.detail}
          successIcon={false}
          closeModal={handleOk}
          errorIcon={false}
        />
      )}
    </aside>
  );
};

export default SideMenu;
